import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Link,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import LockIcon from '@mui/icons-material/Lock'
import CloudIcon from '@mui/icons-material/Cloud'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Media } from 'service/media'
import { useNavigate } from 'react-router-dom'
import { Path } from 'routes'
import { Api } from 'service/api'
import { ReferralEnum } from 'client'
import { logErr, logInfo, logWarn } from 'log'
import { getErrorMessage } from 'utils'

const FinancesLanding = () => {
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)

  const benefits = [
    'Extract financial data directly from client meetings',
    'Auto-populate client profiles with retirement plans, assets, and more',
    'Seamlessly integrate with your existing PreciseFP workflow',
    'Reduce data entry time by up to 90%'
  ]

  function onClickConnectPreciseFP() {
    navigate(Path.PrecisefpSettings)
  }

  async function onSignupClick() {
    try {
      const api = await Api()
      await api.referrals.log(ReferralEnum.PRECISEFP_SIGNUP)
    } catch (e) {
      const err = getErrorMessage(e)
      logErr('PreciseFP onSignupClick', { err })
    }
  }

  async function onConnectClick() {
    try {
      const api = await Api()
      await api.referrals.log(ReferralEnum.PRECISEFP_CONTACT)
      setDialogOpen(true)
    } catch (e) {
      const err = getErrorMessage(e)
      logErr('PreciseFP onConnectClick', { err })
    }
  }

  return (
    <>
      {/* Feature Banner: TODO: rm after Apr 30th 2025 */}
      <Box
        sx={{
          bgcolor: 'primary.light',
          py: 1,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <StarIcon sx={{ color: 'white', fontSize: 18, mr: 1 }} />
        <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
          New Feature: Automatic financial data extraction from client meetings
        </Typography>
      </Box>

      <CardContent sx={{ p: 4 }}>
        <Grid2 container spacing={4}>
          {/* Left Column - Content */}
          <Grid2 size={{ xs: 12, md: 7 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Connect PreciseFP and Build Client&apos;s Financial Data
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Automatically generate detailed financial data based on client meetings, ready for immediate integration with PreciseFP. Save time and reduce errors by eliminating manual data entry.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              Benefits:
            </Typography>

            <List disablePadding sx={{ mb: 3 }}>
              {benefits.map((benefit, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={benefit}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>

            <Chip
              icon={<LockIcon sx={{ fontSize: '1rem !important' }} />}
              label="Connection required to access this feature"
              size="small"
              sx={{ mb: 1.5 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Don&apos;t have a PreciseFP account?{' '}
              <Link
                href="https://www.precisefp.com/signup"
                onClick={onSignupClick}
                underline="hover"
                color="primary"
                sx={{ cursor: 'pointer' }}
              >
                Start a 14-day free trial
                <OpenInNewIcon color="primary" sx={{ paddingLeft: "3px", fontSize: 14 }} />
              </Link>
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudIcon />}
                sx={{ borderRadius: 1.5, px: 2.5, py: 1 }}
                onClick={onClickConnectPreciseFP}
              >
                Connect PreciseFP
              </Button>

              <Link
                component="button"
                variant="body2"
                onClick={onConnectClick}
                underline="hover"
                color="primary"
                sx={{ cursor: 'pointer', textAlign: 'left', p: 0 }}
              >
                Get in touch with PreciseFP
              </Link>
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                height: '100%',
                minHeight: { xs: '240px', md: '100%' },
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <Box
                component="img"
                src={Media.sampleFinancials}
                alt="PreciseFP Integration"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
              />
            </Box>
          </Grid2>
        </Grid2>
      </CardContent>
      {/* Success dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          PreciseFP Contact Request Received
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            We will get you in contact with a member from PreciseFP.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary" autoFocus>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default FinancesLanding